import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { updateUser, clearUser } from "../feature/user.Slice";
import OptIn from "../components/Optin";
import * as XLSX from "xlsx";
import { QRCodeCanvas } from "qrcode.react";
import EmailChangeModal from "../components/EmailChangeModal";

const regexEmail = /^[^@\s]{2,30}@[^\s@]{2,30}\.[^@\s]{2,}$/;
const regexPassword =
  /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*+.~/-]).{8,}$/;
const forbiddenEmailPatterns = ["intradef.gouv.fr"];

const containsForbiddenPattern = (email, appNumber) => {
  if (appNumber !== 5) return false;
  const lowerEmail = email.toLowerCase();
  return forbiddenEmailPatterns.some((pattern) =>
    lowerEmail.includes(pattern.toLowerCase())
  );
};

const apiPrefix = process.env.REACT_APP_API_PREFIX;

export const shouldUpdateProfileUser = (data) =>
  data?.message === "Mise à jour réussie";

export const normalizeProfileEmail = (email) =>
  String(email || "").trim().toLowerCase();

export const formatLastActive = (lastActive) => {
  return !lastActive || new Date(lastActive).getFullYear() === 1970
    ? "Pas d'activité"
    : new Date(lastActive).toLocaleString();
};

export const buildQrDownloadFilename = (companyName) => {
  const sanitized = (companyName || "entreprise")
    .replace(/[^a-zA-Z0-9-_]/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 50);
  return `inscription-${sanitized}.png`;
};

const Profile = () => {
  const Numberapp = useSelector((state) => state.app.appNumber);
  const [selectedTab, setSelectedTab] = useState("profile");
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.user?.user) || {};
  const token = useSelector((state) => state.user.token);
  const optInStatus = user?.optin;
  const [error, setError] = useState("");
  const [username, setUsername] = useState(user.username);
  const [password, setPassword] = useState("");
  const [firstname, setFirstname] = useState(user.firstname);
  const [lastname, setLastname] = useState(user.lastname);
  const [company] = useState(user.company);
  const [siret] = useState(user.siret);
  const [users, setUsers] = useState([]);
  const [signupUrl, setSignupUrl] = useState("");
  const urlRef = useRef(null);
  const qrRef = useRef(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [usersToDelete, setUsersToDelete] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [usernameError, setUsernameError] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [emailChange, setEmailChange] = useState(null);
  const [emailChangeError, setEmailChangeError] = useState("");
  const [emailChangeSubmitting, setEmailChangeSubmitting] = useState(false);

  const handleUsernameChange = (e) => {
    setUsername(e.target.value);
    if (containsForbiddenPattern(e.target.value, Numberapp)) {
      setUsernameError(
        "Cette adresse e-mail n'est pas autorisée sur cette plateforme."
      );
    } else {
      setUsernameError("");
    }
  };

  useEffect(() => {
    if (selectedTab === "other") {
      const fetchData = async () => {
        console.log({ siret, company, appNumber: Numberapp });

        try {
          const response = await fetch(apiPrefix + "users/filter", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ siret, company, appNumber: Numberapp }),
          });

          if (response.ok) {
            const data = await response.json();
            setUsers(data);
          } else {
            console.error(
              "Erreur lors de la récupération des utilisateurs",
              response.statusText
            );
          }
        } catch (error) {
          console.error(
            "Erreur lors de la récupération des utilisateurs",
            error
          );
        }
      };

      fetchData();

      const fetchSignupLink = async () => {
        try {
          const response = await fetch(apiPrefix + "signup-link", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ appNumber: Numberapp }),
          });
          const data = await response.json().catch(() => ({}));
          if (response.ok && data.url) {
            setSignupUrl(data.url);
          }
        } catch (linkError) {
          console.error("Erreur lors de la création du lien d'inscription", linkError);
        }
      };

      fetchSignupLink();
    }
  }, [selectedTab, siret, company, Numberapp, token]);

  useEffect(() => {
    setUsername(user.username);
    setFirstname(user.firstname);
    setLastname(user.lastname);
  }, [user]);

  const authHeaders = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    "X-App-Number": String(Numberapp),
  };

  const startEmailChange = async (newEmail) => {
    const response = await fetch(apiPrefix + "account/email-change/request", {
      method: "POST",
      headers: authHeaders,
      body: JSON.stringify({ newEmail, appNumber: Numberapp }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || "Impossible de lancer le changement d'email.");
    }
    setEmailChangeError("");
    setEmailChange({
      step: data.step || "old",
      pendingEmail: data.pendingEmail || newEmail,
      maskedEmail: data.maskedEmail,
      expiresAt: data.expiresAt,
    });
  };

  const handleEmailChangeVerify = async (code) => {
    setEmailChangeSubmitting(true);
    setEmailChangeError("");
    try {
      const response = await fetch(apiPrefix + "account/email-change/verify", {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ code, appNumber: Numberapp }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Code invalide.");
      }
      if (data.step === "completed") {
        dispatch(
          updateUser({
            id: user.id,
            username: data.username,
            firstname: user.firstname,
            lastname: user.lastname,
            lastactivity: user.lastactive,
            is_admin: user.is_admin,
          })
        );
        setUsername(data.username);
        setEmailChange(null);
        setIsEditing(false);
        setPassword("");
        return;
      }
      setEmailChange((current) => ({
        ...current,
        step: data.step,
        pendingEmail: data.pendingEmail || current?.pendingEmail,
        maskedEmail: data.maskedEmail,
        expiresAt: data.expiresAt,
      }));
    } catch (err) {
      setEmailChangeError(err.message);
    } finally {
      setEmailChangeSubmitting(false);
    }
  };

  const handleEmailChangeResend = async () => {
    setEmailChangeSubmitting(true);
    setEmailChangeError("");
    try {
      const response = await fetch(apiPrefix + "account/email-change/resend", {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ appNumber: Numberapp }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Impossible de renvoyer le code.");
      }
      setEmailChange((current) => ({
        ...current,
        step: data.step || current?.step,
        maskedEmail: data.maskedEmail,
        expiresAt: data.expiresAt,
      }));
    } catch (err) {
      setEmailChangeError(err.message);
    } finally {
      setEmailChangeSubmitting(false);
    }
  };

  const handleEmailChangeCancel = async () => {
    try {
      await fetch(apiPrefix + "account/email-change/cancel", {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ appNumber: Numberapp }),
      });
    } catch (err) {
      console.error("Erreur annulation changement email:", err);
    }
    setEmailChange(null);
    setEmailChangeError("");
    setUsername(user.username);
  };

  const handleUpdate = (e) => {
    e.preventDefault();

    const emailChanged =
      normalizeProfileEmail(username) !== normalizeProfileEmail(user.username);
    const profileChanged =
      firstname !== user.firstname ||
      lastname !== user.lastname ||
      Boolean(password);

    if (containsForbiddenPattern(username, Numberapp)) {
      setError(
        "Cette adresse e-mail n'est pas autorisée sur cette plateforme."
      );
      return;
    }

    if (!regexEmail.test(username)) {
      setError("Le nom d'utilisateur doit être une adresse e-mail valide.");
      return;
    }

    if (!emailChanged || profileChanged) {
      if (!regexPassword.test(password)) {
        setError(
          "Le mot de passe doit contenir au moins 8 caractères, dont une majuscule, une minuscule, un chiffre et un caractère spécial."
        );
        return;
      }
    }

    setError("");

    const updateProfile = () => {
      if (!profileChanged && emailChanged) {
        return Promise.resolve({ skipped: true });
      }

      return fetch(apiPrefix + `update/${user.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          password,
          firstname,
          lastname,
          appNumber: Numberapp,
        }),
      }).then((response) => response.json());
    };

    updateProfile()
      .then(async (data) => {
        if (data?.skipped !== true && !shouldUpdateProfileUser(data) && profileChanged) {
          return;
        }
        if (shouldUpdateProfileUser(data)) {
          dispatch(
            updateUser({
              id: user.id,
              username: user.username,
              firstname,
              lastname,
              lastactivity: user.lastactive,
              is_admin: user.is_admin,
            })
          );
          if (!emailChanged) {
            setIsEditing(false);
            setPassword("");
          }
        }
        if (emailChanged) {
          try {
            await startEmailChange(username);
          } catch (emailError) {
            setError(emailError.message);
          }
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        dispatch(clearUser());
      });
  };

  // formatLastActive utilitaire exporté

  const handleCopyUrl = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(signupUrl);
      } else {
        // Fallback pour les navigateurs plus anciens
        urlRef.current.select();
        document.execCommand("copy");
      }
    } catch (error) {
      console.error("Erreur lors de la copie:", error);
      // Fallback si l'API clipboard échoue
      urlRef.current.select();
      document.execCommand("copy");
    }
  };

  const handleDownloadQrCode = () => {
    const canvas = qrRef.current;
    if (!canvas) {
      return;
    }
    const pngUrl = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.download = buildQrDownloadFilename(company);
    link.href = pngUrl;
    link.click();
  };

  const handleOpenDeleteModal = () => {
    setSelectedUsers([]);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedUsers([]);
  };

  const handleToggleUser = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map((u) => u.id));
    }
  };

  const handleValidateSelection = () => {
    const selected = users.filter((u) => selectedUsers.includes(u.id));
    setUsersToDelete(selected);
    setShowDeleteModal(false);
    setShowConfirmModal(true);
  };

  const handleCloseConfirmModal = () => {
    setShowConfirmModal(false);
    setUsersToDelete([]);
    setSelectedUsers([]);
  };

  const handleConfirmDeletion = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch(apiPrefix + "request-account-deletion", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          chefEntreprise: {
            name: `${user.firstname} ${user.lastname}`,
            email: user.username,
            company: user.company,
            siret: user.siret,
          },
          usersToDelete: usersToDelete.map((u) => ({
            id: u.id,
            email: u.username,
            firstname: u.firstname,
            lastname: u.lastname,
            company: u.company,
            siret: u.siret,
            creationDate: u.creationDate,
            lastactive: u.lastactive,
          })),
          appNumber: Numberapp,
        }),
      });

      if (response.ok) {
        setShowConfirmModal(false);
        setUsersToDelete([]);
        setSelectedUsers([]);
        alert("Demande de suppression envoyée avec succès.");
      } else {
        const error = await response.json();
        console.error("Erreur lors de l'envoi de la demande:", error);
        alert("Une erreur est survenue lors de l'envoi de la demande.");
      }
    } catch (error) {
      console.error("Erreur lors de l'envoi de la demande:", error);
      alert("Une erreur est survenue lors de l'envoi de la demande.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const exportToExcel = () => {
    const fieldsToExport = [
      "id",
      "username",
      "firstname",
      "lastname",
      "lastactive",
      "company",
      "siret",
      "creationDate",
    ];

    // Filtrer les champs par utilisateur
    const filteredUsers = users.map((user) => {
      const filtered = {};
      fieldsToExport.forEach((field) => {
        filtered[field] = user[field];
      });
      return filtered;
    });

    const worksheet = XLSX.utils.json_to_sheet(filteredUsers);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Utilisateurs");

    XLSX.writeFile(workbook, "users.xlsx");
  };

  const isApp5 = Numberapp === 5;

  return (
    <div className="min-h-screen bg-gray-50 py-8 md:py-12 px-4 md:px-6">
      <div className="max-w-4xl mx-auto">
        <div className="card-theme mb-6 md:mb-8">
          <div className="flex flex-col sm:flex-row border-b border-gray-200">
            <button
              className={`px-4 md:px-6 py-3 md:py-4 text-sm md:text-base font-medium transition-colors duration-200 ${
                selectedTab === "profile"
                  ? "text-theme-primary border-b-2 border-theme-primary bg-theme-primary/5"
                  : "text-gray-500 hover:text-theme-primary hover:bg-gray-50"
              }`}
              onClick={() => setSelectedTab("profile")}
            >
              <i className="fas fa-user mr-2"></i>
              Profil
            </button>
            {user.chefEntreprise && (
              <button
                className={`px-4 md:px-6 py-3 md:py-4 text-sm md:text-base font-medium transition-colors duration-200 ${
                  selectedTab === "other"
                    ? "text-theme-primary border-b-2 border-theme-primary bg-theme-primary/5"
                    : "text-gray-500 hover:text-theme-primary hover:bg-gray-50"
                }`}
                onClick={() => setSelectedTab("other")}
              >
                <i className="fas fa-building mr-2"></i>
                Accès chef d'Entreprise
              </button>
            )}
          </div>
        </div>

        {selectedTab === "profile" && (
          <div className="space-y-6 md:space-y-8">
            <div className="card-theme">
              <div className="flex justify-between items-center mb-6 md:mb-8">
                <h1 className="text-2xl md:text-3xl font-bold text-theme-primary">
                  Profil
                </h1>
                {!isEditing && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="btn-theme-secondary text-sm"
                  >
                    <i className="fas fa-pen mr-2"></i>
                    Modifier
                  </button>
                )}
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                  {error}
                </div>
              )}

              {!isEditing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <div className="flex items-center p-4 md:p-5 bg-gray-50 rounded-lg transition-colors hover:bg-gray-100">
                    <i className="fas fa-user text-theme-accent mr-3 text-lg md:text-xl"></i>
                    <div>
                      <span className="text-xs md:text-sm text-gray-600">
                        Nom d'utilisateur
                      </span>
                      <p className="font-semibold text-sm md:text-base">
                        {user.username}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center p-4 md:p-5 bg-gray-50 rounded-lg transition-colors hover:bg-gray-100">
                    <i className="fas fa-user-tag text-theme-accent mr-3 text-lg md:text-xl"></i>
                    <div>
                      <span className="text-xs md:text-sm text-gray-600">
                        Prénom
                      </span>
                      <p className="font-semibold text-sm md:text-base">
                        {user.firstname}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center p-4 md:p-5 bg-gray-50 rounded-lg transition-colors hover:bg-gray-100">
                    <i className="fas fa-id-badge text-theme-accent mr-3 text-lg md:text-xl"></i>
                    <div>
                      <span className="text-xs md:text-sm text-gray-600">
                        Nom de famille
                      </span>
                      <p className="font-semibold text-sm md:text-base break-all">
                        {user.lastname}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center p-4 md:p-5 bg-gray-50 rounded-lg transition-colors hover:bg-gray-100">
                    <i className="fas fa-building text-theme-accent mr-3 text-lg md:text-xl"></i>
                    <div>
                      <span className="text-xs md:text-sm text-gray-600">
                        Société
                      </span>
                      <p className="font-semibold text-sm md:text-base">
                        {user.company}
                      </p>
                    </div>
                  </div>

                  {!isApp5 && (
                    <div className="flex items-center p-4 md:p-5 bg-gray-50 rounded-lg transition-colors hover:bg-gray-100">
                      <i className="fas fa-id-card text-theme-accent mr-3 text-lg md:text-xl"></i>
                      <div>
                        <span className="text-xs md:text-sm text-gray-600">
                          Siret
                        </span>
                        <p className="font-semibold text-sm md:text-base">
                          {user.siret}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <form
                  onSubmit={handleUpdate}
                  className="space-y-5 md:space-y-6"
                >
                  <p className="text-sm md:text-base text-gray-600">
                    Les champs marqués d'un astérisque (*) sont obligatoires
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <i className="fas fa-user mr-2 text-theme-accent"></i>
                        Nom d'utilisateur
                      </label>
                      <input
                        type="text"
                        value={username}
                        onChange={handleUsernameChange}
                        className={`input-theme ${
                          usernameError ? "border-2 border-red-500" : ""
                        }`}
                      />
                      {usernameError && (
                        <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-3 mt-2 rounded flex items-start">
                          <i className="fas fa-exclamation-triangle mr-2 mt-0.5 text-red-600"></i>
                          <span className="text-sm font-medium">
                            {usernameError}
                          </span>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <i className="fas fa-key mr-2 text-theme-accent"></i>
                        Mot de passe {normalizeProfileEmail(username) === normalizeProfileEmail(user.username) ? "*" : ""}
                      </label>
                      <input
                        type="password"
                        value={password}
                        placeholder="******"
                        onChange={(e) => setPassword(e.target.value)}
                        className="input-theme"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <i className="fas fa-user-tag mr-2 text-theme-accent"></i>
                        Prénom
                      </label>
                      <input
                        type="text"
                        value={firstname}
                        onChange={(e) => setFirstname(e.target.value)}
                        className="input-theme"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <i className="fas fa-user mr-2 text-theme-accent"></i>
                        Nom de famille
                      </label>
                      <input
                        type="text"
                        value={lastname}
                        onChange={(e) => setLastname(e.target.value)}
                        className="input-theme"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditing(false);
                        setUsername(user.username);
                        setFirstname(user.firstname);
                        setLastname(user.lastname);
                        setPassword("");
                        setUsernameError("");
                        setError("");
                      }}
                      className="btn-theme-secondary flex-1"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      disabled={
                        normalizeProfileEmail(username) ===
                          normalizeProfileEmail(user.username) && !password
                      }
                      className={`btn-theme flex-1 ${
                        normalizeProfileEmail(username) ===
                          normalizeProfileEmail(user.username) && !password
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      Enregistrer
                    </button>
                  </div>
                </form>
              )}
            </div>
            <div className="card-theme">
              <h1 className="text-xl md:text-2xl font-bold text-theme-primary mb-3 md:mb-4">
                Abonnez-vous à notre Newsletter
              </h1>
              <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6">
                Recevez les dernières nouvelles et mises à jour directement dans
                votre boîte de réception.
              </p>
              <OptIn username={username} optIn={optInStatus} />
            </div>
          </div>
        )}

        {selectedTab === "other" && (
          <div className="space-y-6">
            {signupUrl && (
              <div className="card-theme p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h2 className="text-lg font-semibold text-gray-800 mb-3">
                  <i className="fas fa-link mr-2 text-theme-primary"></i>
                  URL d'inscription
                </h2>
                <p className="text-sm text-gray-600 mb-3">
                  Partagez cette URL avec vos employés pour qu'ils puissent
                  créer leur compte. Les informations de votre entreprise seront
                  automatiquement pré-remplies.
                </p>
                <div className="flex gap-2">
                  <input
                    ref={urlRef}
                    value={signupUrl}
                    readOnly
                    className="input-theme flex-1 font-mono text-sm"
                  />
                  <button
                    className="btn-theme-secondary"
                    onClick={handleCopyUrl}
                  >
                    <i className="fas fa-copy mr-2"></i>
                    Copier
                  </button>
                </div>
                <div className="mt-6 pt-6 border-t border-blue-200">
                  <h3 className="text-base font-semibold text-gray-800 mb-2">
                    <i className="fas fa-qrcode mr-2 text-theme-primary"></i>
                    QR code d'inscription
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Scannez ce code ou téléchargez-le pour le partager avec vos
                    employés.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
                    <div className="bg-white p-3 rounded-lg border border-gray-200">
                      <QRCodeCanvas
                        ref={qrRef}
                        value={signupUrl}
                        size={180}
                        level="M"
                        includeMargin
                      />
                    </div>
                    <button
                      type="button"
                      className="btn-theme-secondary"
                      onClick={handleDownloadQrCode}
                    >
                      <i className="fas fa-download mr-2"></i>
                      Télécharger le QR code
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div className="card-theme">
              <div className="flex justify-between items-center mb-4">
                <h1 className="text-2xl font-bold text-theme-primary mb-0">
                  Ma Société
                </h1>
                <button
                  onClick={handleOpenDeleteModal}
                  className="btn-theme-secondary"
                >
                  <i className="fas fa-user-times mr-2"></i>
                  Demander la suppression de comptes
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {users.map((user) => (
                  <div key={user.id} className="bg-gray-50 p-4 rounded-lg">
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <i className="fas fa-user text-theme-accent mr-2"></i>
                        <span className="font-semibold">
                          {user.firstname} {user.lastname}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <i className="fas fa-envelope text-theme-accent mr-2"></i>
                        <span className="text-sm text-gray-600">
                          {user.username}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <i className="fas fa-building text-theme-accent mr-2"></i>
                        <span className="text-sm text-gray-600">
                          {user.company}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <i className="fas fa-clock text-theme-accent mr-2"></i>
                        <span className="text-sm text-gray-600">
                          {formatLastActive(user.lastactive)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={exportToExcel} className="btn-theme-secondary">
                <i className="fas fa-download mr-2"></i>
                Exporter les utilisateurs en Excel
              </button>
            </div>

            {showDeleteModal && (
              <div className="modal-overlay" onClick={handleCloseDeleteModal}>
                <div
                  className="modal-content"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    className="absolute top-2 right-3 text-gray-500 hover:text-black text-xl"
                    onClick={handleCloseDeleteModal}
                  >
                    ×
                  </button>
                  <div>
                    <h2 className="text-2xl font-bold text-theme-primary mb-4">
                      <i className="fas fa-user-times mr-2"></i>
                      Sélectionner les comptes à supprimer
                    </h2>
                    <p className="text-gray-600 mb-4">
                      Cochez les comptes pour lesquels vous souhaitez demander
                      la suppression.
                    </p>

                    <div className="mb-4">
                      <button
                        onClick={handleSelectAll}
                        className="btn-theme-secondary text-sm"
                      >
                        {selectedUsers.length === users.length
                          ? "Tout désélectionner"
                          : "Tout sélectionner"}
                      </button>
                      <span className="ml-4 text-gray-600">
                        {selectedUsers.length} compte(s) sélectionné(s) sur{" "}
                        {users.length}
                      </span>
                    </div>
                    <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4 mb-4">
                      {users.length === 0 ? (
                        <p className="text-gray-500 text-center py-4">
                          Aucun utilisateur trouvé
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {users.map((user) => (
                            <label
                              key={user.id}
                              className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={selectedUsers.includes(user.id)}
                                onChange={() => handleToggleUser(user.id)}
                                className="mr-3 w-4 h-4 text-theme-primary"
                              />
                              <div className="flex-1">
                                <div className="font-medium text-gray-800">
                                  {user.firstname} {user.lastname}
                                </div>
                                <div className="text-sm text-gray-600">
                                  {user.username}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  Dernière activité :{" "}
                                  {formatLastActive(user.lastactive)}
                                </div>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-3 justify-end">
                      <button
                        onClick={handleCloseDeleteModal}
                        className="btn-theme-secondary"
                      >
                        Annuler
                      </button>
                      <button
                        onClick={handleValidateSelection}
                        disabled={selectedUsers.length === 0}
                        className="btn-theme disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Valider la sélection ({selectedUsers.length})
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {showConfirmModal && (
              <div className="modal-overlay" onClick={handleCloseConfirmModal}>
                <div
                  className="modal-content"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    className="absolute top-2 right-3 text-gray-500 hover:text-black text-xl"
                    onClick={handleCloseConfirmModal}
                  >
                    ×
                  </button>
                  <div>
                    <h2 className="text-2xl font-bold text-theme-primary mb-4">
                      <i className="fas fa-exclamation-triangle mr-2 text-yellow-500"></i>
                      Confirmation de la demande de suppression
                    </h2>
                    <p className="text-gray-700 mb-4">
                      Vous êtes sur le point de demander la suppression de{" "}
                      <strong>{usersToDelete.length}</strong> compte(s).
                    </p>
                    <p className="text-gray-600 mb-4">
                      Cette demande sera envoyée à notre équipe qui procédera à
                      la suppression des comptes sélectionnés.
                    </p>
                    <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
                      <h3 className="font-semibold text-gray-800 mb-3">
                        Récapitulatif des comptes à supprimer :
                      </h3>
                      <div className="space-y-2">
                        {usersToDelete.map((user) => (
                          <div
                            key={user.id}
                            className="p-2 bg-white rounded border border-gray-200"
                          >
                            <div className="font-medium text-gray-800">
                              {user.firstname} {user.lastname}
                            </div>
                            <div className="text-sm text-gray-600">
                              {user.username}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-3 justify-end">
                      <button
                        onClick={handleCloseConfirmModal}
                        className="btn-theme-secondary"
                        disabled={isSubmitting}
                      >
                        Retour
                      </button>
                      <button
                        onClick={handleConfirmDeletion}
                        disabled={isSubmitting}
                        className="btn-theme disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <>
                            <i className="fas fa-spinner fa-spin mr-2"></i>
                            Envoi en cours...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-paper-plane mr-2"></i>
                            Confirmer et envoyer
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {emailChange && (
        <EmailChangeModal
          step={emailChange.step}
          maskedEmail={emailChange.maskedEmail}
          pendingEmail={emailChange.pendingEmail}
          error={emailChangeError}
          isSubmitting={emailChangeSubmitting}
          onVerify={handleEmailChangeVerify}
          onResend={handleEmailChangeResend}
          onCancel={handleEmailChangeCancel}
        />
      )}
    </div>
  );
};

export default Profile;
